// Widget i18n — dependency-free string catalog + lookup.
//
// The active locale is resolved ONCE from the clinic-config response via
// `setWidgetLocale`. Clinic-config is prefetched at embed mount (see
// `EmbedShell.tsx` + `lib/clinicConfig.ts`), so the launcher localizes as soon
// as config lands — before first open. Until then the default is `en` (a brief
// English flash on slow networks is accepted per
// SPECS/patient-path-localization.md). Unknown locale values fall back to `en`.

import { en } from './strings.en';
import { nlBE } from './strings.nl-BE';

export type SupportedLocale = 'en' | 'nl-BE';

export type Catalog = typeof en;

// Keys whose catalog value is a plain string (resolved via `t`).
export type StringKey = {
  [K in keyof Catalog]: Catalog[K] extends readonly string[] ? never : K;
}[keyof Catalog];

// Keys whose catalog value is a string list (resolved via `tList`).
export type ListKey = {
  [K in keyof Catalog]: Catalog[K] extends readonly string[] ? K : never;
}[keyof Catalog];

const CATALOGS: Record<SupportedLocale, Catalog> = {
  en,
  'nl-BE': nlBE,
};

let activeLocale: SupportedLocale = 'en';

/** Normalize an arbitrary config value to a supported locale (unknown → `en`). */
export function resolveLocale(value: string | null | undefined): SupportedLocale {
  return value === 'nl-BE' ? 'nl-BE' : 'en';
}

/** Set the active locale once, from the clinic-config response. */
export function setWidgetLocale(value: string | null | undefined): void {
  activeLocale = resolveLocale(value);
}

export function getWidgetLocale(): SupportedLocale {
  return activeLocale;
}

function interpolate(input: string, params: Record<string, string | number>): string {
  return input.replace(/\{(\w+)\}/g, (_match, token: string) =>
    token in params ? String(params[token]) : `{${token}}`,
  );
}

/** Resolve a plain-string key in the active locale (falls back to `en`). */
export function t(key: StringKey, params?: Record<string, string | number>): string {
  const value = (CATALOGS[activeLocale][key] ?? en[key]) as string;
  return params ? interpolate(value, params) : value;
}

/** Resolve a string-list key in the active locale (falls back to `en`). */
export function tList(key: ListKey): readonly string[] {
  return (CATALOGS[activeLocale][key] ?? en[key]) as readonly string[];
}
